import { useEffect, useState, type ReactNode } from "react";
import { MAX_ATTEMPTS, type TimedHistoryEntry } from "@mk/shared";
import { Board } from "./Board.tsx";
import { ConfirmDialog } from "./ConfirmDialog.tsx";
import { DuelClock } from "./DuelClock.tsx";
import { Keyboard } from "./Keyboard.tsx";
import { ResultEffects } from "./ResultEffects.tsx";
import { ScreenHeader } from "./ScreenHeader.tsx";
import { ShareButton } from "./ShareButton.tsx";
import { SoloModeTabs } from "./SoloModeTabs.tsx";
import { TimedFriendsRanking } from "./TimedFriendsRanking.tsx";
import { TimedRecentRuns } from "./TimedRecentRuns.tsx";
import { TimedRunReport } from "./TimedRunReport.tsx";
import { Toast } from "./Toast.tsx";
import type { SoloMode } from "../features/solo/soloMode.ts";
import { useTimedSoloGame } from "../features/solo/useTimedSoloGame.ts";
import { timedShareResult } from "../features/share/shareText.ts";
import { useBoardSelectionHint } from "../features/useBoardSelectionHint.ts";
import { messages } from "../i18n/index.ts";
import { leaveResultThroughAd } from "../lib/adGate.ts";
import type { RegisterBackHandler } from "../lib/navigation.ts";
import { useOnline } from "../lib/pwa.ts";
import { useSession } from "../lib/session.ts";

interface TimedSoloViewProps {
  onBack: () => void;
  onExitConfirmed: () => void;
  onModeChange: (mode: SoloMode) => void;
  onSignIn: (reason: string) => void;
  registerBackHandler: RegisterBackHandler;
}

export function TimedSoloView({
  onBack,
  onExitConfirmed,
  onModeChange,
  onSignIn,
  registerBackHandler,
}: TimedSoloViewProps) {
  const status = useSession((store) => store.status);
  const online = useOnline();
  const enabled = status === "authed" && online;
  const game = useTimedSoloGame(enabled);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [report, setReport] = useState<TimedHistoryEntry | null>(null);
  const live =enabled && (game.phase === "live" || game.phase === "finishing");
  const selectionHint = useBoardSelectionHint(game.inputDisabled ? null : game.pinned);

  useEffect(
    () =>
      registerBackHandler(() => {
        if (report) {
          setReport(null);
          return "block";
        }
        if (leaveOpen) {
          setLeaveOpen(false);
          return "block";
        }
        if (enabled && game.phase === "live") {
          setLeaveOpen(true);
          return "block";
        }
        if (enabled && game.phase === "result") {
          void leaveResultThroughAd().then(onExitConfirmed);
          return "block";
        }
        return "allow";
      }),
    [enabled, game.phase, leaveOpen, onExitConfirmed, registerBackHandler, report],
  );

  const shell = (main: ReactNode, subtitle = messages.solo.timedSubtitle) => (
    <div className="app solo-app">
      <ScreenHeader title={messages.solo.title} subtitle={subtitle} onBack={onBack} />
      <SoloModeTabs value="timed" onChange={onModeChange} />
      {main}
      <TimedRunReport run={report} onClose={() => setReport(null)} />
    </div>
  );

  if (!online) {
    return shell(
      <main className="grid place-items-center px-6 text-center">
        <div className="timed-notice">
          <p className="eyebrow">{messages.solo.timedOfflineTitle}</p>
          <p className="mt-2 text-sm text-muted">{messages.solo.timedOfflineHint}</p>
        </div>
      </main>,
    );
  }

  if (status === "anon") {
    return shell(
      <main className="grid place-items-center px-6 text-center">
        <div className="timed-notice">
          <p className="eyebrow">{messages.solo.timedSignInTitle}</p>
          <p className="mt-2 text-sm leading-relaxed text-muted">{messages.solo.timedSignInHint}</p>
          <button type="button" className="btn-primary mt-4 w-full" onClick={() => onSignIn(messages.solo.timedSignInReason)}>
            {messages.solo.signIn}
          </button>
        </div>
      </main>,
    );
  }

  if (status === "loading" || game.phase === "checking") {
    return shell(
      <main className="grid place-items-center">
        <p className="text-sm text-muted">{messages.common.loading}</p>
      </main>,
    );
  }

  if (game.phase === "idle" || game.phase === "starting") {
    return shell(
      <main className="overflow-y-auto px-4 pb-6">
        <div className="mx-auto flex w-full max-w-md flex-col gap-4 pt-2">
          <section className="timed-start">
            <p className="timed-start__clock">3:00</p>
            <p className="text-sm leading-relaxed">{messages.solo.timedRules}</p>
            <p className="mt-1 text-xs text-muted">{messages.solo.timedTieBreak}</p>
            <button type="button" className="btn-primary mt-4 w-full" disabled={game.phase === "starting"} onClick={game.start}>
              {game.phase === "starting" ? messages.solo.timedStarting : messages.solo.timedStart}
            </button>
            {game.error && <p className="mt-2 text-sm text-flame" role="alert">{game.error}</p>}
          </section>
          <TimedRecentRuns refreshKey="start" onOpen={setReport} />
          <TimedFriendsRanking refreshKey="start" />
        </div>
      </main>,
    );
  }

  if (game.phase === "result" && game.result) {
    const result = game.result;
    // Altı hakta bulunamayan kelime koşuyu bitirir; o zaman açık kelime kalmaz.
    const outOfGuesses = !result.unfinished && result.rounds.at(-1)?.solved === false;
    return shell(
      <main className="overflow-y-auto px-4 pb-6">
        <ResultEffects matchId={`timed:${result.runId}`} outcome={result.isNewRecord ? "win" : "draw"} />
        <div className="mx-auto flex w-full max-w-md flex-col gap-4 pt-2">
          <section className="team-result-hero" data-outcome={result.isNewRecord ? "win" : undefined}>
            <p className="eyebrow">{outOfGuesses ? messages.solo.timedOutOfGuesses : messages.solo.timedTimeUp}</p>
            <div className="timed-result__count">{result.wordsSolved}</div>
            <h1 className="timed-result__title">{messages.solo.timedWordsFound(result.wordsSolved)}</h1>
            <p className="text-sm text-muted">{messages.solo.timedAttempts(result.solvedAttempts)}</p>
            {result.isNewRecord ? (
              <p className="timed-result__record">{messages.solo.timedNewRecord}</p>
            ) : result.best.wordsSolved > 0 && (
              <p className="mt-2 text-sm text-muted">{messages.solo.timedBest(result.best.wordsSolved, result.best.solvedAttempts)}</p>
            )}
          </section>

          <button
            type="button"
            className="btn-primary"
            onClick={() => { void leaveResultThroughAd().then(game.start); }}
          >
            {messages.solo.timedPlayAgain}
          </button>
          <ShareButton rows={[]} result={timedShareResult(result, result.isNewRecord)} />

          <section>
            <h2 className="timed-section-title">{messages.solo.timedRoundsTitle}</h2>
            {result.rounds.length === 0 && !result.unfinished ? (
              <p className="text-sm text-muted">{messages.solo.timedNoRounds}</p>
            ) : (
              <ol className="timed-rounds">
                {result.rounds.map((round) => (
                  <li key={round.wordIndex} data-solved={round.solved ? "true" : "false"}>
                    <b>{round.answer}</b>
                    <span>{round.solved ? `${round.guesses.length}/${MAX_ATTEMPTS}` : "✕"}</span>
                  </li>
                ))}
                {result.unfinished && (
                  <li data-solved="unfinished">
                    <b>{result.unfinished.answer}</b>
                    <span>{messages.solo.timedUnfinished(result.unfinished.guesses.length)}</span>
                  </li>
                )}
              </ol>
            )}
          </section>

          <TimedRecentRuns refreshKey={result.runId} onOpen={setReport} />
          <TimedFriendsRanking refreshKey={result.runId} />
        </div>
      </main>,
      messages.solo.timedResultTitle,
    );
  }

  if (!live || !game.state || !game.clock) return shell(<main />);

  const { state, clock } = game;
  const finishing = game.phase === "finishing";
  const notice = game.flash
    ? game.flash.solved ? messages.solo.timedBonus(game.flash.answer) : messages.solo.timedNextWord(game.flash.answer)
    : finishing ? messages.solo.timedResultLoading : game.toast ?? selectionHint;

  return (
    <div className="app solo-app">
      <ScreenHeader title={messages.solo.title} subtitle={messages.solo.timedSubtitle} onBack={() => setLeaveOpen(true)} />
      <div className="team-scorebar">
        <div><span>{messages.solo.timedWords}</span><b>{state.wordsSolved}</b></div>
        <DuelClock remainingMs={clock.remainingMs} canShorten={false} />
        <div><span>{messages.solo.timedWordNumber}</span><b>{state.wordIndex + 1}</b></div>
      </div>
      <main className="team-live-main">
        <Toast message={notice} />
        <div className="team-own-board">
          {clock.countdownSecond !== null && (
            <div className="team-countdown-overlay" role="status"><span>{clock.countdownSecond}</span></div>
          )}
          <Board
            rows={game.rows}
            cells={game.flash ? [] : game.cells}
            pinned={game.flash ? null : game.pinned}
            onSelectCell={game.selectCell}
            shake={game.shake}
            won={game.flash?.solved ?? false}
            maxAttempts={MAX_ATTEMPTS}
          />
        </div>
      </main>
      <Keyboard states={game.keyboard} onPress={game.press} disabled={game.inputDisabled && !game.sending} busy={game.sending} />

      <ConfirmDialog
        open={leaveOpen}
        title={messages.solo.timedLeaveTitle}
        message={messages.solo.timedLeaveMessage}
        confirmLabel={messages.solo.timedLeave}
        busy={leaving}
        onCancel={() => setLeaveOpen(false)}
        onConfirm={() => {
          setLeaving(true);
          void game.end().then(() => {
            setLeaving(false);
            setLeaveOpen(false);
            onExitConfirmed();
          });
        }}
      />
    </div>
  );
}
